<template>
  <div id="doAnswerPage">
    <a-card>
      <h1>{{ app.appName }}</h1>
      <p>{{ app.appDesc }}</p>
      <h2 style="margin-bottom: 32px">
        {{ current }}、{{ currentQuestion?.title }}
      </h2>
      <div>
        <a-radio-group
          direction="vertical"
          v-model="currentAnswer"
          :options="questionOptions"
          @change="doRadioChange"
        ></a-radio-group>
      </div>
      <div style="margin-top: 24px">
        <a-space>
          <a-button
            type="primary"
            circle
            v-if="current < questionContent.length"
            :disabled="!currentAnswer"
            @click="current += 1"
            >下一题</a-button
          >
          <a-button
            type="primary"
            v-if="current === questionContent.length"
            circle
            :loading="submitting"
            :disabled="!currentAnswer"
            @click="handleSubmit"
            >{{ submitting ? "评分中" : "查看结果" }}</a-button
          >
          <a-button v-if="current > 1" circle @click="current -= 1"
            >上一题</a-button
          >
        </a-space>
      </div>
    </a-card>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  defineProps,
  reactive,
  ref,
  watchEffect,
  withDefaults,
} from "vue";
import { useRouter } from "vue-router";
import API from "@/api";
import { listQuestionVoByPageUsingPost } from "@/api/questionController";
import { Message } from "@arco-design/web-vue";
import { getAppVoByIdUsingGet } from "@/api/appController";
import {
  addUserAnswerUsingPost,
  generateUserAnswerIdUsingGet,
} from "@/api/userAnswerController";

const router = useRouter();

interface Props {
  appId: string;
}

// 唯一 id
const id = ref<number>();

// 生成唯一id
const generateId = async () => {
  const res = await generateUserAnswerIdUsingGet();
  if (res.data.code === 0) {
    id.value = res.data.data as number;
  } else {
    Message.error("获取唯一 id 失败，" + res.data.message);
  }
};

watchEffect(() => {
  generateId();
});

const props = withDefaults(defineProps<Props>(), {
  appId: () => {
    return "";
  },
});

const app = ref<API.AppVO>({});

// 题目内容结构
const questionContent = ref<API.QuestionContentDTO[]>([]);

// 当前题目的需要（从1开始）
const current = ref(1);
// 当前题目
const currentQuestion = ref<API.QuestionContentDTO>({});
// 当前题目选项
const questionOptions = computed(() => {
  return currentQuestion.value?.options
    ? currentQuestion.value.options.map((option) => {
        return {
          label: `${option.key}. ${option.value}`,
          value: option.key,
        };
      })
    : [];
});
// 当前答案
const currentAnswer = ref<string>();
// 回答列表
const answerList = reactive<string[]>([]);
// 是否正在提交
const submitting = ref(false);

const loadData = async () => {
  if (!props.appId) {
    return;
  }
  // 获取app
  let res = await getAppVoByIdUsingGet({
    id: props.appId as any,
  });
  if (res.data.code === 0) {
    app.value = res.data.data as any;
  } else {
    Message.error("获取应用失败," + res.data.message);
  }

  // 获取题目
  let res1 = await listQuestionVoByPageUsingPost({
    appId: props.appId as any,
    current: 1,
    pageSize: 1,
    sortField: "createTime",
    sortOrder: "descend",
  });
  if (res1.data.code === 0 && res1.data.data?.records) {
    questionContent.value = res1.data.data?.records[0].questionContent as any;
  } else {
    Message.error("获取题目失败，" + res.data.message);
  }
};

watchEffect(() => {
  loadData();
});

watchEffect(() => {
  currentQuestion.value = questionContent.value[current.value - 1];
  currentAnswer.value = answerList[current.value - 1];
});

/*
 * 选中选项号，保存值
 * */
const doRadioChange = (value: string) => {
  answerList[current.value - 1] = value;
};

/*
 * 提交
 * */
const handleSubmit = async () => {
  if (!props.appId || !answerList) {
    return;
  }
  submitting.value = true;
  const res = await addUserAnswerUsingPost({
    appId: props.appId as any,
    choices: answerList,
    id: id.value as any,
  });
  if (res.data.code === 0 && res.data.data) {
    setTimeout(() => {
      router.push({
        path: `/answer/result/${res.data.data}`,
      });
    }, 500);
  } else {
    Message.error("提交答案失败，" + res.data.message);
  }
  submitting.value = false;
};
</script>

<style scoped></style>
